/**
 * Student Portal — IELTS Preparation Progress
 * Mounted on /student/dashboard when the student has any IELTS activity
 * (or has selected IELTS as a preparation goal). Non-fabricating —
 * skills without attempts show "Not assessed yet".
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Award, TrendingUp, Target, ChevronRight,
  Headphones, BookOpen, PenSquare, Mic,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

const SKILL_META = {
  listening: { label: 'Listening', icon: Headphones, gradient: 'from-blue-500 to-cyan-500' },
  reading:   { label: 'Reading',   icon: BookOpen,   gradient: 'from-indigo-500 to-violet-500' },
  writing:   { label: 'Writing',   icon: PenSquare,  gradient: 'from-emerald-500 to-teal-500' },
  speaking:  { label: 'Speaking',  icon: Mic,        gradient: 'from-orange-500 to-rose-500' },
};

export default function StudentIeltsSection() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [mocks, setMocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [targetInput, setTargetInput] = useState(7.0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        ax.get('/api/ielts/v2/me/ielts-progress'),
        ax.get('/api/ielts/v2/mock/history').catch(() => ({ data: { items: [] } })),
      ]);
      setData(r.data);
      setMocks(m.data.items || []);
      setTargetInput(r.data?.target_band || 7.0);
    } catch (e) {
      // Silent — section is optional
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveTarget = async () => {
    try {
      await ax.post('/api/ielts/v2/me/target-band', { target_band: Number(targetInput) });
      toast.success(`Target band set to ${Number(targetInput).toFixed(1)}`);
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save target');
    }
  };

  if (loading) return null;
  if (!data) return null;

  const anyAssessed = Object.values(data.skills || {}).some((s) => s.assessed);
  if (!anyAssessed && !data.target_band && mocks.length === 0) return null;  // hide until student engages

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6 space-y-5"
       data-testid="student-ielts-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-indigo-500 font-bold">IELTS Preparation</div>
          <h2 className="text-xl md:text-2xl font-black text-[#172033]">Your IELTS Progress</h2>
          <div className="text-xs text-slate-500 mt-0.5">
            {data.variant_label} · <span className="italic">{data.disclaimer}</span>
          </div>
        </div>
        <button onClick={() => nav('/resources/ielts')}
          data-testid="student-ielts-open-hub"
          className="text-xs font-bold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1">
          Open IELTS Hub <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Overall + target */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-purple-500 text-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-indigo-100 font-bold flex items-center gap-1">
            <Award className="h-3.5 w-3.5" /> Overall estimated band
          </div>
          <div className="text-5xl font-black tabular-nums mt-1"
             data-testid="student-ielts-overall">
            {data.overall_estimated_band != null ? data.overall_estimated_band.toFixed(1) : '—'}
          </div>
          <div className="text-[11px] text-indigo-100 mt-1">
            {data.overall_estimated_band != null ? 'Avg of assessed skills' : 'Take a simulation to get started'}
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
            <Target className="h-3.5 w-3.5" /> Target band
          </div>
          {editing ? (
            <div className="mt-2 flex items-center gap-2">
              <input type="number" step="0.5" min="1" max="9"
                data-testid="student-ielts-target-input"
                value={targetInput}
                onChange={(e) => setTargetInput(e.target.value)}
                className="w-20 border rounded px-2 py-1 text-lg font-bold" />
              <button onClick={saveTarget} data-testid="student-ielts-target-save"
                className="px-3 py-1 rounded bg-indigo-500 text-white text-xs font-bold">Save</button>
              <button onClick={() => setEditing(false)}
                className="px-2 py-1 text-xs text-slate-500">Cancel</button>
            </div>
          ) : (
            <div className="mt-1 flex items-baseline justify-between">
              <div className="text-5xl font-black tabular-nums text-[#172033]"
                data-testid="student-ielts-target">
                {data.target_band != null ? Number(data.target_band).toFixed(1) : '—'}
              </div>
              <button onClick={() => setEditing(true)}
                data-testid="student-ielts-target-edit"
                className="text-xs font-bold text-indigo-600 hover:text-indigo-800">
                {data.target_band != null ? 'Edit' : 'Set target'}
              </button>
            </div>
          )}
          <div className="text-[11px] text-slate-500 mt-1">Prep goal, not a guaranteed score</div>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5" /> Recommended next
          </div>
          <div className="text-sm font-bold text-[#172033] mt-1">{data.recommendation?.label || 'Keep practising'}</div>
          <div className="text-xs text-slate-600 mt-1">{data.recommendation?.reason}</div>
          {data.recommendation?.route && (
            <button onClick={() => nav(data.recommendation.route)}
              data-testid="student-ielts-recommendation-cta"
              className="mt-2 text-xs font-bold text-indigo-600 hover:text-indigo-800">Practise now →</button>
          )}
        </div>
      </div>

      {/* Skill cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
        data-testid="student-ielts-skills">
        {['listening', 'reading', 'writing', 'speaking'].map((k) => {
          const s = data.skills[k]; const meta = SKILL_META[k]; const Icon = meta.icon;
          const goRoute = k === 'listening' ? '/resources/ielts/listening/simulation'
                            : k === 'reading' ? `/resources/ielts/reading/simulation?variant=${data.variant}`
                            : k === 'writing' ? '/resources/ielts/writing'
                            : k === 'speaking' ? '/resources/ielts/speaking'
                            : '/resources/ielts';
          return (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid={`student-ielts-skill-${k}`}>
              <div className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r ${meta.gradient} text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest`}>
                <Icon className="h-3 w-3" /> {meta.label}
              </div>
              {s.assessed ? (
                <>
                  <div className="text-4xl font-black tabular-nums text-[#172033] mt-2"
                    data-testid={`student-ielts-skill-${k}-band`}>
                    {s.latest_band?.toFixed(1)}
                  </div>
                  <div className="text-[11px] text-slate-500">latest estimate</div>
                  {s.previous_band != null && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      prev {s.previous_band.toFixed(1)}
                      {s.latest_band > s.previous_band && <span className="ml-1 text-emerald-600 font-bold">↑</span>}
                      {s.latest_band < s.previous_band && <span className="ml-1 text-rose-600 font-bold">↓</span>}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-500 mt-1">
                    {s.attempts} attempt{s.attempts === 1 ? '' : 's'}
                  </div>
                  {s.trend && s.trend.length > 1 && (
                    <div className="mt-2 flex items-end gap-0.5 h-6"
                      data-testid={`student-ielts-skill-${k}-trend`}>
                      {s.trend.slice(-8).map((t, i) => (
                        <div key={i} className={`w-2 rounded-t bg-gradient-to-t ${meta.gradient}`}
                          title={`Attempt ${i+1}: Band ${t.band}`}
                          style={{ height: `${(t.band / 9) * 100}%` }} />
                      ))}
                    </div>
                  )}
                  <button onClick={() => nav(goRoute)}
                    data-testid={`student-ielts-skill-${k}-practice`}
                    className="mt-2 text-xs font-bold text-indigo-600 hover:text-indigo-800">Practise again →</button>
                </>
              ) : (
                <>
                  <div className="text-sm font-bold text-slate-400 mt-3">Not assessed yet</div>
                  {['listening', 'reading', 'writing', 'speaking'].includes(k) ? (
                    <button onClick={() => nav(goRoute)}
                      data-testid={`student-ielts-skill-${k}-start`}
                      className="mt-2 text-xs font-bold text-indigo-600 hover:text-indigo-800">
                      {k === 'writing' ? 'Start Writing →' : k === 'speaking' ? 'Start Speaking →' : 'Start simulation →'}
                    </button>
                  ) : (
                    <div className="text-[11px] text-slate-400 mt-1">Coming soon</div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Strengths + focus */}
      {(data.strengths?.length || data.focus_areas?.length) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="student-ielts-strengths">
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">Your strengths</div>
            {(data.strengths || []).length ? (
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {data.strengths.map((s, i) => <li key={i}>✓ {s.label}{s.band ? ` (${s.band.toFixed(1)})` : ''}</li>)}
              </ul>
            ) : <div className="mt-2 text-xs text-slate-500 italic">Complete more simulations to identify strengths.</div>}
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="student-ielts-focus">
            <div className="text-[10px] uppercase tracking-widest text-rose-800 font-bold">Focus areas</div>
            {(data.focus_areas || []).length ? (
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {data.focus_areas.map((s, i) => <li key={i}>→ {s.label}{s.band ? ` (${s.band.toFixed(1)})` : ''}</li>)}
              </ul>
            ) : <div className="mt-2 text-xs text-slate-500 italic">No focus areas yet — keep practising.</div>}
          </div>
        </div>
      ) : null}

      {/* Full Mock attempts */}
      <div className="rounded-xl border border-slate-200 p-4" data-testid="student-ielts-mocks">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Full Mock Attempts</div>
          <button onClick={() => nav('/resources/ielts/mock')}
            data-testid="student-ielts-mock-cta"
            className="text-xs font-bold text-indigo-600 hover:text-indigo-800">
            Take Full Mock →
          </button>
        </div>
        {mocks.length === 0 ? (
          <div className="mt-2 text-xs text-slate-500 italic" data-testid="student-ielts-mocks-empty">
            No full mocks yet. A full mock covers Listening + Reading + Writing + Speaking and gives you a complete Winhouse Estimated IELTS Practice Band.
          </div>
        ) : (
          <div className="mt-2 divide-y divide-slate-100">
            {mocks.slice(0, 5).map((m) => (
              <div key={m.id} className="py-2 flex items-center gap-3 text-sm"
                data-testid={`student-ielts-mock-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{m.title}</div>
                  <div className="text-[11px] text-slate-500">
                    {new Date(m.started_at).toLocaleDateString()} · {m.test_variant === 'academic' ? 'Academic' : 'General Training'} · {m.status === 'submitted' ? 'Completed' : 'In progress'}
                    {m.skills ? ` · L${m.skills.listening?.toFixed(1)} R${m.skills.reading?.toFixed(1)} W${m.skills.writing?.toFixed(1)} S${m.skills.speaking?.toFixed(1)}` : ''}
                  </div>
                </div>
                {m.overall_band != null && (
                  <div className="text-right">
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">Overall</div>
                    <div className="text-lg font-bold text-slate-900">{m.overall_band.toFixed(1)}</div>
                  </div>
                )}
                <button onClick={() => nav(m.status === 'submitted' ? `/resources/ielts/mock/results/${m.id}` : `/resources/ielts/mock/session/${m.id}`)}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800"
                  data-testid={`student-ielts-mock-open-${m.id}`}>
                  {m.status === 'submitted' ? 'View Results' : 'Resume'} →
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent activity */}
      {data.recent_activity?.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="student-ielts-recent">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Recent IELTS activity</div>
          <div className="mt-2 divide-y divide-slate-100">
            {data.recent_activity.map((a) => (
              <div key={a.attempt_id} className="py-2 flex items-center justify-between gap-3 text-sm"
                data-testid={`student-ielts-recent-${a.attempt_id}`}>
                <div>
                  <div className="font-bold text-[#172033] text-xs">{a.test_title}</div>
                  <div className="text-[11px] text-slate-500">
                    {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : '—'} · {a.raw_score}/{a.total_questions}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-lg font-black tabular-nums text-indigo-600">
                    {a.estimated_band != null ? Number(a.estimated_band).toFixed(1) : '—'}
                  </div>
                  {a.results_route && (
                    <button onClick={() => nav(a.results_route)}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-800">View</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
